import { Form, Formik } from 'formik'
import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import * as Yup from 'yup'
import { useApiEnableUser, useApiResetPassword } from '../../api/account/apiUsers'
import { ApiErrorBody, ErrorCode } from '../../api/apiUtils'
import { ApiError } from '../../generated'
import { Button } from '../../genericComponents/Button'
import { Card } from '../../genericComponents/Card'
import { Note } from '../../genericComponents/Note'
import { Input } from '../../inputs/Input'
import { INPUT_FORM_COMMON_SIZE } from '../../genericComponents/OverlayWrapper'
import { FlexContainer } from '../../genericComponents/layout/FlexContainer'

export const PasswordErrors: Record<ErrorCode, string> = {
  MIN_LENGTH: 'Password must be at least eight characters long.',
  MAX_LENGTH: 'Maximum password length exceeded.',
  DIGIT_CHAR: 'Password must contain at least one digit.',
  SPECIAL_CHAR: 'Password must contain at least one special character.',
  UPPER_CASE_CHAR: 'Password must contain at least one upper case character.',
  PASSWORD_MUST_BE_DIFFERENT: 'New password must be different than the old password.',
  PASSWORD_DOES_NOT_MATCH: 'Current password is not correct',
}

type Props = { mode: 'resetPassword' | 'enableUser' }

export const ResetPasswordCard: React.FC<Props> = (props: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  if (!token) {
    return (
      <Card title="">
        <h1 className="text-primaryFontColor text-xl uppercase text-center tracking-widest font-bold text-alertColor">
          Url incorrect. Token missing
        </h1>
      </Card>
    )
  }

  const navigate = useNavigate()

  const { mutate: resetPassword } = useApiResetPassword()
  const { mutate: enableUser } = useApiEnableUser()
  const apiCall = props.mode == 'resetPassword' ? resetPassword : enableUser

  const validationSchema = Yup.object().shape({
    newPassword: Yup.string()
      .required('New Password is required')
      .min(8, PasswordErrors.MIN_LENGTH)
      .matches(/\d+/gi, PasswordErrors.DIGIT_CHAR)
      .matches(/[!@#$%^&*()_+]/gi, PasswordErrors.SPECIAL_CHAR)
      .matches(/[A-Z]+/g, PasswordErrors.UPPER_CASE_CHAR),
    newPasswordAgain: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Password confirmation is required'),
  })

  const handleChangePassword = (
    values: { currentPassword: string; newPassword: string; newPasswordAgain: string },
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    setErrorMessages([])
    setErrorMessage(null)
    //handle change password
    apiCall(
      { newPassword: values.newPassword, token },
      {
        onSuccess() {
          void navigate('/password_changed')
        },
        onError: ((error: ApiError) => {
          const subErrors = (error.body as ApiErrorBody).apierror.subErrors?.[0]
          const softlockErrors = subErrors?.softlockErrors

          if (softlockErrors.length > 0) {
            setErrorMessages(softlockErrors.map((v) => PasswordErrors[v]))
          } else {
            setErrorMessage((error.body as ApiErrorBody).apierror.message)
          }
          setSubmitting(false)
        }) as (error: unknown, variables: unknown, context: unknown) => void,
      }
    )
  }

  return (
    <Card title="">
      <FlexContainer
        align="items-center"
        gap="gap-0"
        justify="justify-center"
        className={'flex-col ' + INPUT_FORM_COMMON_SIZE}
      >
        <h1 className="text-primaryFontColor text-xl uppercase text-center tracking-widest font-bold">
          Welcome to HMD Softlock
        </h1>
        <h2 className="text-primaryFontColor text-lg font-semibold text-center m-6">
          Change password
        </h2>

        <p className="self-center">
          You signed in with a temporary password. Please change your password to continue.
        </p>

        <Formik
          initialValues={{ currentPassword: '', newPassword: '', newPasswordAgain: '' }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => handleChangePassword(values, setSubmitting)}
        >
          {({ isSubmitting }) => (
            <Form>
              <div>
                <Note className="mt-4">
                  The password must contain at least 8 alphanumeric characters, at least one number
                  and one uppercase letter and have at least one special character.
                </Note>
                <Input name="newPassword" type="password" label="New Password" />
                {errorMessages &&
                  errorMessages.map((errorMessage) => (
                    <div className="flex">
                      <span className="text-alertColor text-sm font-semibold italic">
                        {errorMessage}
                      </span>
                    </div>
                  ))}
                <Input name="newPasswordAgain" type="password" label="Repeat New Password" />
                {errorMessage && (
                  <div className="flex">
                    <span className="text-alertColor text-sm font-semibold italic">
                      {errorMessage}
                    </span>
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-primary text-white mt-4"
                >
                  CHANGE PASSWORD
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </FlexContainer>
    </Card>
  )
}
